// src/components/VideoGallery/VideoGallery.js

import React, { useState, useEffect } from 'react';
import axios from 'axios';
import './VideoGallery.css'; // We will use the new, improved CSS file
import Footer from '../Footer/Footer';
import Navbar from '../Navbar/Navbar';

// --- Helper Function to get all metadata (thumbnail, embed URL) ---
const getVideoMetadata = async (url) => {
  let videoId;
  
  // --- YouTube ---
  const youtubeMatch = url.match(/(?:https?:\/\/)?(?:www\.)?(?:youtube\.com\/(?:[^\/\n\s]+\/\S+\/|(?:v|e(?:mbed)?)\/|\S*?[?&]v=)|youtu\.be\/)([a-zA-Z0-9_-]{11})/);
  if (youtubeMatch) {
    videoId = youtubeMatch[1];
    return {
      type: 'youtube',
      embedUrl: `https://www.youtube.com/embed/${videoId}?autoplay=1&rel=0`,
      // High-quality thumbnail, with a fallback
      thumbnailUrl: `https://img.youtube.com/vi/${videoId}/maxresdefault.jpg`,
      fallbackThumbnailUrl: `https://img.youtube.com/vi/${videoId}/sddefault.jpg`,
    };
  }

  // --- Vimeo ---
  const vimeoMatch = url.match(/(?:https:\/\/)?(?:www\.)?vimeo\.com\/(?:channels\/(?:\w+\/)?|groups\/(?:[^\/]*)\/videos\/|album\/(?:\d+)\/video\/|)(\d+)/);
  if (vimeoMatch) {
    videoId = vimeoMatch[1];
    try {
      // Vimeo requires an API call to get the thumbnail
      const response = await axios.get(`https://vimeo.com/api/v2/video/${videoId}.json`);
      const vimeoData = response.data[0];
      return {
        type: 'vimeo',
        embedUrl: `https://player.vimeo.com/video/${videoId}?autoplay=1`,
        thumbnailUrl: vimeoData.thumbnail_large,
        title: vimeoData.title, // Vimeo API provides the title!
      };
    } catch (error) {
      console.error('Failed to fetch Vimeo metadata:', error);
      // Fallback if Vimeo API fails
      return { type: 'vimeo', embedUrl: `https://player.vimeo.com/video/${videoId}?autoplay=1`, thumbnailUrl: null };
    }
  }
  
  // Unsupported URL
  return { type: 'unknown', embedUrl: null, thumbnailUrl: null };
};


function VideoGallery() {
  const [videos, setVideos] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [modalVideoUrl, setModalVideoUrl] = useState(null);

  const API_BASE_URL = process.env.REACT_APP_API_URL || 'https://api.hitkarihospital.com:5005'; 

  useEffect(() => {
    const fetchAndProcessVideos = async () => {
      try {
        setLoading(true);
        setError(null);
        
        // 1. Fetch video URLs from your backend
        const response = await axios.get(`${API_BASE_URL}/api/videos`);
        const rawVideos = response.data;

        // 2. Fetch metadata (thumbnails, etc.) for each video in parallel
        const processedVideos = await Promise.all(
          rawVideos.map(async (video) => {
            const metadata = await getVideoMetadata(video.videoUrl);
            return {
              ...video, // original data like _id, videoUrl
              ...metadata, // new data like thumbnailUrl, embedUrl
            };
          })
        );
        
        // Filter out any videos we couldn't process
        setVideos(processedVideos.filter(v => v.type !== 'unknown'));

      } catch (err) {
        console.error("Error fetching videos:", err);
        setError("Failed to load videos. Please try again later.");
      } finally {
        setLoading(false);
      }
    };

    fetchAndProcessVideos();
  }, [API_BASE_URL]);

  const openModal = (embedUrl) => {
    if (embedUrl) {
      setModalVideoUrl(embedUrl);
    }
  };

  const closeModal = () => {
    setModalVideoUrl(null);
  };
  
  // Fallback for broken YouTube thumbnails
  const handleImageError = (e) => {
      // If maxresdefault fails, try the fallback sddefault
      if (e.target.src.includes('maxresdefault.jpg')) {
          const fallbackSrc = e.target.dataset.fallback;
          if(fallbackSrc) e.target.src = fallbackSrc;
      } else {
          // If even the fallback fails, use a placeholder
          e.target.src = 'https://i.imgur.com/sdw2i5c.jpeg'; // Generic placeholder
      }
  };

  if (loading) {
    return (
      <div id="vediogallery-hit-loader-container" className="vediogallery-hit-centered-container">
        <div className="vediogallery-hit-loader"></div>
        <p>Loading Videos...</p>
      </div>
    );
  }

  if (error) {
    return (
      <div id="vediogallery-hit-error-container" className="vediogallery-hit-centered-container">
        <p className="vediogallery-hit-error-message">{error}</p>
      </div>
    );
  }

  return (
    <>
    <Navbar/>
    <div id="vediogallery-hit-page">
      <div className="vediogallery-hit-header">
        <h1>Our Video Gallery</h1>
        <p>Watch patient testimonials, health tips, and hospital updates.</p>
      </div>

      {videos.length === 0 ? (
        <div className="vediogallery-hit-centered-container">
          <p>No videos have been added yet.</p>
        </div>
      ) : (
        <div id="vediogallery-hit-grid">
          {videos.map((video, index) => (
            <div
              key={video._id}
              className="vediogallery-hit-card"
              style={{ animationDelay: `${index * 50}ms` }}
              onClick={() => openModal(video.embedUrl)}
              tabIndex="0"
              onKeyPress={(e) => e.key === 'Enter' && openModal(video.embedUrl)}
            >
              <div className="vediogallery-hit-thumbnail-container">
                <img 
                    src={video.thumbnailUrl || 'https://i.imgur.com/sdw2i5c.jpeg'} 
                    alt={video.title || 'Video Thumbnail'}
                    className="vediogallery-hit-thumbnail"
                    onError={handleImageError}
                    data-fallback={video.fallbackThumbnailUrl} // Store fallback URL
                />
                <div className="vediogallery-hit-play-icon">
                    <svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 24 24"><path d="M8 5v14l11-7z"/></svg>
                </div>
              </div>
              {video.title && (
                <div className="vediogallery-hit-title-bar">
                    <p>{video.title}</p>
                </div>
              )}
            </div>
          ))}
        </div>
      )}

      {/* Video Playback Modal */}
      {modalVideoUrl && (
        <div id="vediogallery-hit-modal" onClick={closeModal}>
          <div className="vediogallery-hit-modal-content" onClick={(e) => e.stopPropagation()}>
            <button id="vediogallery-hit-modal-close" onClick={closeModal}>×</button>
            <div className="vediogallery-hit-video-wrapper">
              <iframe
                src={modalVideoUrl}
                title="Video Player"
                frameBorder="0"
                allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture"
                allowFullScreen
              ></iframe>
            </div>
          </div>
        </div>
      )}
    </div>
    <Footer/>
    </>
  );
}

export default VideoGallery;